import { z } from "zod";
import {
    useForm,
    FormProvider,
    useFormContext,
    Controller,
    useFieldArray,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Info, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { FileUploadInput } from "@/components/FileUploadInput";
import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { authHeaders, Axios } from "@/utils/axios";
import { useUserStore } from "@/store/userStore";
import { API_END_POINTS } from "@/utils/routes";
import LoaderOverlay from "@/components/common/LoaderOverlay";
import { CRYPTO_CURRENCY_OPTIONS, CURRENCY_OPTIONS, DURATIONUNIT_LABEL, JOB_APPLICATION_STATUS_WITH_KEY, PAYMENT_METHOD_OPTIONS } from "@/utils/constant";
import { PaymentMethodType } from "@/utils/enum";
import { contractDetails} from "@/store/contractStore";

const enterpriseSchema = z.object({
    paymentTerm: z
        .string()
        .trim()
        .min(1, "Payment term is required"),
    durationValue: z
      .string()
      .trim()
      .min(1, "Contract duration is required")
      .refine((val) => Number(val) > 0, {
        message: "Contract duration cannot be negative",
      }),
    durationUnit: z
      .string()
      .trim()
      .min(1, "Duration unit is required"),
    startDate: z
        .string()
        .trim()
        .optional()
        .transform((val) => (val ? new Date(val).toISOString() : val)),
    endDate: z
        .string()
        .trim()
        .optional()
        .transform((val) => (val ? new Date(val).toISOString() : val)),
    value: z
        .string()
        .trim()
        .min(1, "Contract amount is required")
        .refine((val) => Number(val) >= 0, {
            message: "Contract amount cannot be negative",
        }),
    terms: z.string().trim().optional(),
    paymentMethod: z.string().trim().min(1, "Payment method is required"),
    currency: z.string().trim().optional(),
    cryptotoken: z.string().trim().optional(),
    milestones: z
        .array(
            z.object({
                title: z.string().trim().min(1, "Title is required"),
                amount: z.string()
                    .trim()
                    .min(1, "Amount is required")
                    .refine((val) => Number(val) >= 0, {
                        message: "Amount value cannot be negative",
                    }),
                status: z.string().trim().min(1, "Status is required"),
                startDate: z
                    .string()
                    .trim()
                    .min(1, "Start date is required")
                    .transform((val) => new Date(val).toISOString()),
                dueDate: z
                    .string()
                    .trim()
                    .min(1, "End date is required")
                    .transform((val) => new Date(val).toISOString()),
                description: z.string().trim().optional(),
            })
        )
        .optional(),
    notes: z.string().trim().optional(),
    contractFile: z
        .instanceof(File)
        .or(z.null())
        .refine((file) => file !== null, {
            message: "Contract form is required",
        }),
}).superRefine((data, ctx) => {
    if (data.paymentMethod === "paypal") {
        if (!data.currency || data.currency.trim().length < 1) {
            ctx.addIssue({
                path: ["currency"],
                code: z.ZodIssueCode.custom,
                message: "Currency is required",
            });
        }
    }
    if (data.paymentMethod === "crypto") {
        if (!data.cryptotoken || data.cryptotoken.trim().length < 1) {
            ctx.addIssue({
                path: ["cryptotoken"],
                code: z.ZodIssueCode.custom,
                message: "Crypto token is required",
            });
        }
    }
});

type FormData = z.infer<typeof enterpriseSchema>;
const currencyOptions = CURRENCY_OPTIONS;
const cryptoCurrencyOptions = CRYPTO_CURRENCY_OPTIONS;
const durationUnitLabel = DURATIONUNIT_LABEL;
const paymentMethodOptions = PAYMENT_METHOD_OPTIONS;
const jobApplicationStatus = JOB_APPLICATION_STATUS_WITH_KEY;


interface ContractDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    updateApplicationStatusAfterInitiateContract?:  () => void;
    freelancerApplicationID: string | null;
    jobdetailsId: string | null | number;
    updateContractData?: contractDetails | null;
}

export default function ContractDialogWrapper({
    open,
    onOpenChange,
    updateApplicationStatusAfterInitiateContract,
    freelancerApplicationID,
    jobdetailsId,
    updateContractData
}: ContractDialogProps) {
    const methods = useForm<FormData>({
        resolver: zodResolver(enterpriseSchema),
        defaultValues: {
            paymentTerm: "",
            durationValue: "",
            durationUnit: "",
            startDate: "",
            endDate: "",
            value: "",
            terms: "",
            paymentMethod: "crypto", // <-- set default to crypto
            currency: "",
            cryptotoken: "",
            milestones: [],
            notes: "",
            contractFile: null,
        },
        mode: "onSubmit",
    });

    const { toast } = useToast();
    const { user } = useUserStore();
    const [isLoading, setIsLoading] = useState(false);
    const {reset} = methods;

    const handleOpenChange = (isOpen: boolean) => {
        methods.reset();
        onOpenChange(isOpen);
    };

    useEffect(() => {
        if (!updateContractData) return;
        const fetchAndReset = async () => {
            const file = await handleDownloadContract(updateContractData);
            reset({
                paymentTerm: updateContractData?.paymentTerm as string ?? "",
                durationValue: updateContractData?.durationValue?.toString() ?? "",
                durationUnit: updateContractData?.durationUnit as string ?? "",
                startDate: updateContractData?.startDate
                    ? new Date(updateContractData.startDate).toISOString().split("T")[0]
                    : "",
                endDate: updateContractData?.endDate
                    ? new Date(updateContractData.endDate).toISOString().split("T")[0]
                    : "",
                value: updateContractData?.budget?.toString() ?? "",
                terms: updateContractData?.terms ?? "",
                paymentMethod:
                    updateContractData?.paymentMethod?.toString() === "1"
                        ? PaymentMethodType.Paypal
                        : PaymentMethodType.Crypto,
                currency: updateContractData?.currency?.toString() ?? "",
                cryptotoken: updateContractData?.cryptoToken?.toString() ?? "",
                notes: updateContractData?.terms ?? "",
                contractFile: file,
            });
        };
        fetchAndReset();
    }, [updateContractData, reset]);



    const DURATIONUNIT_LABEL_KEY = Object.fromEntries(
        Object.entries(DURATIONUNIT_LABEL).map(([k, v]) => [v, k])
    );

    const onSubmit = async (data: FormData) => {
        if (isLoading) {
            return;
        }
        setIsLoading(true);
        let finalReq = {
            jobId: jobdetailsId,
            freelancerId: freelancerApplicationID,
            budget: data?.value ? Number(data?.value) : null,
            paymentTerm: Number(data?.paymentTerm) ?? 1,
            durationValue: data?.durationValue ? Number(data.durationValue) : null,
            durationUnit: Number(data?.durationUnit) ?? 1,
            startDate: data?.startDate,
            endDate: data?.endDate,
            terms: data?.terms ? data?.terms : null,
            notes: data?.notes ? data?.notes : null,
            paymentMethod: data?.paymentMethod && data?.paymentMethod === PaymentMethodType?.Paypal ? 1 : data?.paymentMethod && data?.paymentMethod === PaymentMethodType?.Crypto ? 2 : null,
            currency: data?.currency ? Number(data?.currency) : null,
            cryptoToken: data?.cryptotoken ? Number(data?.cryptotoken) : null,
            file: data?.contractFile,
            milestones: data?.milestones?.length
                ? data.milestones.map(m => ({
                    ...m,
                    amount: Number(m.amount) || 0,
                    status: Number(m.status) || 0
                }))
                : null,
        }

        try {
            const formData = new FormData();
            for (const key in finalReq) {
                if (!finalReq.hasOwnProperty(key) || !finalReq[key]) continue;
                if (key === 'file') {
                    formData.append('file', finalReq[key]);
                } else if (typeof finalReq[key] === 'object') {
                    formData.append(key, JSON.stringify(finalReq[key]));
                } else {
                    formData.append(key, finalReq[key]);
                }
            }
            const response = updateContractData ? await Axios.put(
                `${API_END_POINTS.CREATE_CONTRACT}/${updateContractData?._id}`,
                formData,
                authHeaders(user?.accessToken)
            ) : await Axios.post(
                API_END_POINTS.CREATE_CONTRACT,
                formData,
                authHeaders(user?.accessToken)
            );
            if (response) {
                setIsLoading(false);
                if (updateApplicationStatusAfterInitiateContract) {
                    await updateApplicationStatusAfterInitiateContract();
                }
                toast({
                    title: updateContractData ? "Contract Updated!" : "Initiate Contract!",
                    description: updateContractData ?  "Contract has been updated successfully" : "Initiate Contract and milestones created successfully.",
                });
            }
            onOpenChange(false);

        } catch (error) {
            onOpenChange(false);
            setIsLoading(false);
            toast({
                title: "Error",
                description: error && error?.response && error?.response?.data?.error ? error?.response?.data?.error :
                    "Failed to initiate contract. Please try again.",
                variant: "destructive",
            });
        }
        methods.reset();
    };

    const handleDownloadContract = async (contractDetails: contractDetails | null) => {
        try {
            const res = await Axios.get(contractDetails?.attachment, {
                responseType: "blob",
            });
            const blob = res.data;
            const fileName =
                contractDetails?.attachment.substring(
                    contractDetails.attachment.lastIndexOf("/") + 1
                ) || "Contract form.pdf";
            const file = new File([blob], fileName, { type: blob.type });
            return file;
        } catch (error) {
            console.error("Error downloading contract:", error);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-[700px] max-h-[90vh] pt-7 pb-7">
                <FormProvider {...methods}>
                    <form onSubmit={methods.handleSubmit(onSubmit)}>
                        <DialogHeader>
                            <DialogTitle>{updateContractData ? 'Update Contract' : 'Initiate Contract'}</DialogTitle>
                            <DialogDescription>
                                {updateContractData ? 'Fill in the details below to update the contract.' : 'Fill in the details below to initiate the contract.'}
                            </DialogDescription>
                        </DialogHeader>
                        {isLoading && <LoaderOverlay />}
                        <ContractFormContent isLoading={isLoading} updateContractData={updateContractData} />
                        <DialogFooter>
                            <Button
                                variant="outline"
                                type="button"
                                disabled={isLoading}
                                onClick={() => handleOpenChange(false)}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isLoading}>{updateContractData ? 'Update Contract' : 'Create Contract'}</Button>
                        </DialogFooter>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    );
}

interface ContractForm {
    isLoading: boolean;
    updateContractData?: contractDetails | null;
}

function ContractFormContent({ isLoading, updateContractData }: ContractForm) {
    const {
        register,
        control,
        formState: { errors },
        watch,
        setValue,
        getValues 
    } = useFormContext<FormData>();
    const { fields, append, remove } = useFieldArray({
        control,
        name: "milestones",
    });
    const [isHideUploadIcon, setIsHideUploadIcon] = useState(false);
    const [mileStoneStart, setMileStoneStart] = useState(null);
    const { toast } = useToast();

    const paymentTerm = watch("paymentTerm");
    const paymentMethod = watch("paymentMethod");
    const start = watch("startDate");
    const end = watch("endDate");
    const durationValue = watch("durationValue")

    const setUploadContractFile = (file : File) =>{
        setIsHideUploadIcon(file ? true : false);
        setValue("contractFile", file)
    }

    const uploadResumeAPI = async (file: File): Promise<any> => {
        if(file){
            setIsHideUploadIcon(true);
        }
    }

    useEffect(() => {
        if (!start) return;

        fields.forEach((_, index) => {
            const milestoneStart = getValues(`milestones.${index}.startDate`);
            if (milestoneStart && milestoneStart < start) {
                // clear or reset to contract start
                setValue(`milestones.${index}.startDate`, start, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                });
            }
        });
    }, [start, fields, getValues, setValue]);

    useEffect(() => {
        if (!end) return;
        fields.forEach((_, index) => {
            const milestoneDueDate = getValues(`milestones.${index}.dueDate`);
            if (milestoneDueDate && milestoneDueDate > end) {
                // clear or reset to contract start
                setValue(`milestones.${index}.dueDate`, end, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                });
            }
            if (milestoneDueDate && milestoneDueDate < mileStoneStart) {
                const newDate = addDays(mileStoneStart, 1)
                setValue(`milestones.${index}.dueDate`, newDate, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                });
            }
        });
    }, [end, mileStoneStart, fields, getValues, setValue]);

    useEffect(() => {
        if (!start) return;
        if (!end || end <= start) {
            setValue("endDate", addDays(start, 1), {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
            });
        }
    }, [start]);
    
    // Helper to add business days (skip Sat & Sun)
    const addBusinessDays = (startDate: string, days: number) => {
        if (!startDate || !days) return startDate;
        let result = new Date(startDate);
        let added = 0;
        while (added < days) {
            const day = result.getDay();
            if (day !== 0 && day !== 6) { // Skip Sunday (0) & Saturday (6)
                added++;
            }
            if (added < days) {
                result.setDate(result.getDate() + 1);
            }
        }
        const year = result.getFullYear();
        const month = String(result.getMonth() + 1).padStart(2, "0");
        const day = String(result.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    // Auto calculate end date if paymentTerm is Daily
    useEffect(() => {
        if (paymentTerm === "2") { // Daily
            const duration = Number(getValues("durationValue"));
            const start = getValues("startDate");
            if (start && duration > 0) {
                const calculatedEnd = addBusinessDays(start, duration);
                setValue("endDate", calculatedEnd, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                });
            }
        }
    }, [paymentTerm, durationValue, start]);

    const addDays = (dateStr: string, days: number) => {
        if (!dateStr) return dateStr;
        const [y, m, d] = dateStr.split("-").map(Number);
        const dt = new Date(Date.UTC(y, m - 1, d));
        dt.setUTCDate(dt.getUTCDate() + days);
        const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
        const dd = String(dt.getUTCDate()).padStart(2, "0");
        return `${dt.getUTCFullYear()}-${mm}-${dd}`;
    };

    const toDateInputValue = (date?: string | Date) => {
        if (!date) return undefined;
        const d = typeof date === "string" ? new Date(date) : date;
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };
    const getStatusColor = (status: string) => {
        switch (status) {
            case "3":
                return "bg-emerald-100 text-emerald-800";
            case "7":
                return "bg-blue-100 text-blue-800";
            case "4":
                return "bg-blue-100 text-blue-800";
            case "1":
                return "bg-gray-100 text-gray-800";
            case "2":
                return "bg-amber-100 text-amber-800";
            case "6":
                return "bg-red-100 text-red-800";
            case "5":
                return "bg-red-100 text-red-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    }

    return (
        <div className="grid gap-4 py-7">
            {/* Payment Terms */}
            <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right">
                    Payment Term <span className="text-red-500">*</span>
                </Label>
                <div className="col-span-3">
                    <Controller
                        control={control}
                        name="paymentTerm"
                        render={({ field }) => (
                            <Select onValueChange={field.onChange} value={field.value}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select payment term" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="1">Fixed</SelectItem>
                                    <SelectItem value="2">Daily</SelectItem>
                                </SelectContent>
                            </Select>
                        )}
                    />
                    {errors.paymentTerm && (
                        <p className="mt-1 text-xs text-red-500">
                            {errors.paymentTerm.message}
                        </p>
                    )}
                </div>
            </div>
            {/* Duration */}
            <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right">
                    Contract Duration <span className="text-red-500">*</span>
                </Label>
                <div className="col-span-3">
                    <Input
                        {...register("durationValue")}
                        placeholder="e.g., 4"
                        className="col-span-3"
                        type="number"
                    />
                    {errors.durationValue  && (
                        <p className="mt-1 text-xs text-red-500">
                            {errors.durationValue.message}
                        </p>
                    )}
                </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right">
                    Duration Unit <span className="text-red-500">*</span>
                </Label>
                <div className="col-span-3">
                    <Controller
                        control={control}
                        name="durationUnit"
                        render={({ field }) => {
                            // If paymentTerm is Daily, set durationUnit to Day(s)
                            useEffect(() => {
                                if (paymentTerm === "2") {
                                    field.onChange("1"); // "1" = Day(s)
                                }
                            }, [paymentTerm]);

                            return (
                                <Select onValueChange={field.onChange} value={field.value}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select duration unit" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {paymentTerm === "2" ? (
                                            <SelectItem value="1">Day(s)</SelectItem>
                                        ) : (
                                            <>
                                                <SelectItem value="1">Day(s)</SelectItem>
                                                <SelectItem value="2">Week(s)</SelectItem>
                                                <SelectItem value="3">Month(s)</SelectItem>
                                            </>
                                        )}
                                    </SelectContent>
                                </Select>
                            );
                        }}
                    />
                    {errors.durationUnit && (
                        <p className="mt-1 text-xs text-red-500">
                            {errors.durationUnit.message}
                        </p>
                    )}
                </div>
            </div>    
            {/* Start Date */}
            <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="startDate" className="text-right">
                    Start Date
                </Label>
                <Input
                    id="startDate"
                    {...register("startDate", {
                        onChange: (e) => {
                            const newStart = e.target.value;
                            const currentEnd = getValues("endDate");
                            if (!currentEnd || currentEnd <= newStart) {
                                setValue("endDate", addDays(newStart, 1), {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                });
                            }
                        },
                    })}
                    type="date"
                    className="col-span-3"
                    min={new Date().toISOString().split("T")[0]}
                />
            </div>
            {/* End Date */}
            <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="endDate" className="text-right">
                    End Date
                </Label>
                <Input
                    id="endDate"
                    {...register("endDate")}
                    type="date"
                    className="col-span-3"
                    min={start ? addDays(start, 1) : undefined}
                    readOnly={paymentTerm === "2"}
                />
            </div>
            {/* Contract Amount */}
            <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right">
                    Contract Amount <span className="text-red-500">*</span>
                </Label>
                <div className="col-span-3">
                    <Input
                        type="number"
                        {...register("value")}
                        placeholder="0.00"
                    />
                    {errors.value && (
                        <p className="mt-1 text-xs text-red-500">
                            {errors.value.message}
                        </p>
                    )}
                </div>
            </div>
            {/* Payment Method */}
            <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right">
                    Payment Method <span className="text-red-500">*</span>
                </Label>
                <div className="col-span-3">
                    <Controller
                        control={control}
                        name="paymentMethod"
                        render={({ field }) => (
                            <Select onValueChange={field.onChange} value={field.value}>
                                <SelectTrigger className="col-span-3">
                                    <SelectValue placeholder="Select payment method" />
                                </SelectTrigger>
                                <SelectContent>
                                    {paymentMethodOptions.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    />
                    {errors.paymentMethod && (
                        <p className="mt-1 text-xs text-red-500">
                            {errors.paymentMethod.message}
                        </p>
                    )}
                </div>
            </div>
            {/* Currency */}
            {paymentMethod && paymentMethod?.toLowerCase() === "paypal" &&
                <div className="grid grid-cols-4 items-center gap-4">
                    <Label className="text-right">
                        Currency <span className="text-red-500">*</span>
                    </Label>
                    <div className="col-span-3">
                        <Controller
                            control={control}
                            name="currency"
                            render={({ field }) => (
                                <Select onValueChange={field.onChange} value={field.value}>
                                    <SelectTrigger className="col-span-3">
                                        <SelectValue placeholder="Select currency" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {currencyOptions.map((option) => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {option.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        />
                        {errors.currency && (
                            <p className="mt-1 text-xs text-red-500">
                                {errors.currency.message}
                            </p>
                        )}
                    </div>
                </div>}
            {/* Crypto Token */}
            {paymentMethod && paymentMethod?.toLowerCase() === "crypto" &&
                <div className="grid grid-cols-4 items-center gap-4">
                    <Label className="text-right">
                        Crypto Token <span className="text-red-500">*</span>
                    </Label>
                    <div className="col-span-3">
                        <Controller
                            control={control}
                            name="cryptotoken"
                            render={({ field }) => (
                                <Select onValueChange={field.onChange} value={field.value}>
                                    <SelectTrigger className="col-span-3">
                                        <SelectValue placeholder="Select crypto token" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {cryptoCurrencyOptions.map((option) => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {option.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        />
                        {errors.cryptotoken && (
                            <p className="mt-1 text-xs text-red-500">
                                {errors.cryptotoken.message}
                            </p>
                        )}
                    </div>
                </div>}
            {/* Milestones */}
            {fields?.length === 0 && !updateContractData && <div className="grid grid-cols-4 gap-4 items-start">
                <Label className="text-right mt-2">Milestones</Label>
                <Button
                    type="button"
                    variant="outline"
                    disabled={isLoading}
                    onClick={() =>
                        append({
                            title: "",
                            amount: "",
                            status: "1",
                            startDate: "",
                            dueDate: "",
                            description: "",
                        })
                    }
                >
                    Add Milestone
                </Button>
            </div>}
            {!updateContractData && fields.map((field, index) => (<div key={field.id} className="grid grid-cols-4 gap-4 items-start">
                <Label className="text-right mt-2">Milestone {index + 1}</Label>
                <div className="col-span-3 space-y-4">
                    <div
                        className="border relative rounded-lg p-4 space-y-3 pr-10 pl-5"
                    >
                        <button
                            type="button"
                            onClick={() => remove(index)}
                            className="absolute top-1 right-1.5 text-red-500 hover:text-red-700"
                        >
                            <X size={18} />
                        </button>
                        {/* Title */}
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label className="text-right col-span-1">
                                Title <span className="text-red-500">*</span>
                            </Label>
                            <div className="col-span-3">
                                <Input
                                    placeholder="Milestone title"
                                    className="col-span-3"
                                    {...register(`milestones.${index}.title`)}
                                />
                                {errors.milestones?.[index]?.title && (
                                    <p className="mt-1 text-xs text-red-500">
                                        {errors.milestones[index]?.title?.message}
                                    </p>
                                )}
                            </div>
                        </div>
                        {/* Amount */}
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label className="text-right col-span-1">
                                Amount <span className="text-red-500">*</span>
                            </Label>
                            <div className="col-span-3">
                                <Input
                                    type="number"
                                    placeholder="0.00"
                                    className="col-span-3"
                                    {...register(`milestones.${index}.amount`)}
                                />
                                {errors.milestones?.[index]?.amount && (
                                    <p className="mt-1 text-xs text-red-500">
                                        {errors.milestones[index]?.amount?.message}
                                    </p>
                                )}
                            </div>
                        </div>
                        {/* Status */}
                        {/* <div className="grid grid-cols-4 items-center gap-4">
                            <Label className="text-right col-span-1">
                                Status <span className="text-red-500">*</span>
                            </Label>
                            <div className="col-span-3">
                                <Controller
                                    control={control}
                                    name={`milestones.${index}.status`}
                                    render={({ field }) => (
                                        <Select
                                            value={field.value?.toString()}
                                            onValueChange={field.onChange}
                                        >
                                            <SelectTrigger>
                                                <Badge className={getStatusColor(field?.value?.toString())}>
                                                    {field.value
                                                        ? jobApplicationStatus.find((s) => s.value?.toString() === field.value?.toString())?.label
                                                        : "Select status"}
                                                </Badge>
                                            </SelectTrigger>
                                            <SelectContent>
                                                {jobApplicationStatus.map((status) => (
                                                    <SelectItem
                                                        key={status.value}
                                                        value={status.value}
                                                    >
                                                        {status.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                                {errors.milestones?.[index]?.status && (
                                    <p className="mt-1 text-xs text-red-500">
                                        {errors.milestones[index]?.status?.message}
                                    </p>
                                )}
                            </div>
                        </div> */}
                        {/* Start Date */}
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label className="text-right col-span-1">
                                Start Date <span className="text-red-500">*</span>
                            </Label>
                            <div className="col-span-3">
                                <Input
                                    type="date"
                                    className="col-span-3"
                                    {...register(`milestones.${index}.startDate`, {
                                        onChange: (e) => {
                                            setMileStoneStart(e.target.value);
                                        },
                                    })}
                                    min={toDateInputValue(watch("startDate")) || new Date().toISOString().split("T")[0]}
                                    max={toDateInputValue(watch("endDate")) || undefined}
                                    placeholder="dd-mm-yyyy" // hint for user
                                />
                                {errors.milestones?.[index]?.startDate && (
                                    <p className="mt-1 text-xs text-red-500">
                                        {errors.milestones[index]?.startDate?.message}
                                    </p>
                                )}
                            </div>
                        </div>
                        {/* Due Date */}
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label className="text-right col-span-1">
                                Due Date <span className="text-red-500">*</span>
                            </Label>
                            <div className="col-span-3">
                                <Input
                                    type="date"
                                    className="col-span-3"
                                    {...register(`milestones.${index}.dueDate`)}
                                    min={
                                        watch(`milestones.${index}.startDate`)
                                            ? toDateInputValue(addDays(watch(`milestones.${index}.startDate`), 1))
                                            : watch("startDate")
                                                ? toDateInputValue(addDays(watch("startDate"), 1))
                                                : undefined
                                    }
                                    max={toDateInputValue(watch("endDate"))}
                                />
                                {errors.milestones?.[index]?.dueDate && (
                                    <p className="mt-1 text-xs text-red-500">
                                        {errors.milestones[index]?.dueDate?.message}
                                    </p>
                                )}
                            </div>
                        </div>
                        {/* Description */}
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label className="text-right col-span-1">Description</Label>
                            <div className="col-span-3">
                                <Textarea
                                    placeholder="Add description"
                                    className="col-span-3"
                                    {...register(`milestones.${index}.description`)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>))}
            {fields?.length >= 1 && !updateContractData && <div className="grid grid-cols-4 gap-4 items-start">
                <Label className="text-right mt-2"></Label>
                <Button
                    type="button"
                    variant="outline"
                    disabled={isLoading}
                    onClick={() =>
                        append({
                            title: "",
                            amount: "",
                            status: "1",
                            startDate: "",
                            dueDate: "",
                            description: "",
                        })
                    }
                >
                    Add Milestone
                </Button>
            </div>}
            {/* Contract file */}
            <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="currency" className="text-right flex items-center justify-end">
                    Contract Form <span className="text-red-500">*</span>
                    <div className="relative group ml-2">
                        <Info className="h-4 w-4 text-gray-400" />
                        <div className="hidden text-start group-hover:block absolute -top-1 left-6 p-2 w-60 text-xs text-gray-600 bg-white rounded shadow-md z-10">
                            Upload your contract form (PDF/DOC, max 5MB).
                        </div>
                    </div>
                </Label>
                <div className="flex flex-col col-span-3">
                    <FileUploadInput
                        label="Contract Form"
                        name="contract_url"
                        buttonText="Upload contract form"
                        tooltipText="Upload your contract form (PDF/DOC, max 5MB)."
                        value={watch("contractFile")}
                        onChange={(file: File) => setUploadContractFile(file)}
                        onUpload={uploadResumeAPI}
                        toast={toast}
                        isHideLabelName={true}
                        isHideUploadIcon={isHideUploadIcon || !!watch("contractFile")}
                    />
                    {errors.contractFile && !watch("contractFile") && (
                        <p className="mt-1 ml-2 text-xs text-red-500">
                            {errors.contractFile?.message}
                        </p>
                    )}
                </div>
            </div>
            {/* Terms */}
            <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="notes" className="text-right">Terms</Label>
                <Textarea
                    id="terms"
                    {...register("terms")}
                    placeholder="Add terms"
                    className="col-span-3"
                />
            </div>
            {/* Notes */}
            <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="notes" className="text-right">Notes</Label>
                <Textarea
                    id="notes"
                    {...register("notes")}
                    placeholder="Add notes"
                    className="col-span-3"
                />
            </div>
        </div>
    );
}
